import { useMemo } from 'react'
import {
  investButtons as staticInvestButtons,
  withdrawButtons as staticWithdrawButtons,
  moreOptionsButtons as staticMoreOptionsButtons,
  InvestButtonConfig,
  WithdrawButtonConfig,
  MoreOptionsButtonConfig
} from '../../utils/mf-execution/buttonsConfig'
import {
  MFExecutionData,
  PaymentPageType,
  RegularPlanSheetVariant,
  SubActionButton,
  SubActionDisabledReason
} from '../../types/mf-execution.types'
import { trackEvent } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'
import { useMetaDataContext } from '../../state-management/contexts/MetaDataContext'

export const useSubActionButtons = (
  data: MFExecutionData,
  handlePaymentPageOpen: (paymentType: PaymentPageType) => void,
  openTransferLearnMoreSheet: () => void,
  openRegularSheet: (variant: RegularPlanSheetVariant) => void
) => {
  const { isin, data: metadata } = useMetaDataContext()
  const fundIsin = isin ?? ''
  const fundName = metadata?.fundDetails?.fundName ?? ''

  const investButtons = useMemo((): SubActionButton[] => {
    return staticInvestButtons.map((button: InvestButtonConfig): SubActionButton => {
      if (button.type === 'one-time') {
        return {
          testLocator: button.testLocator,
          text: button.text,
          icon: button.icon,
          onClick: (e?: React.MouseEvent) => {
            e?.stopPropagation()
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: 'MF_L2',
                eventId: button.testLocator,
                buttonLabel: button.text ?? 'One-time',
                fundIsin,
                fundName
              }
            })
            if (data?.buttonDisabledStates?.buy?.disabledReason === SubActionDisabledReason.REGULAR) {
              openRegularSheet(RegularPlanSheetVariant.INVEST)
              return
            }
            handlePaymentPageOpen(PaymentPageType.BUY)
          },
          isDisabled: data?.buttonDisabledStates?.buy?.isDisabled,
          disabledReason: data?.buttonDisabledStates?.buy?.disabledReason
        }
      } else if (button.type === 'start-sip') {
        return {
          testLocator: button.testLocator,
          text: button.text,
          icon: button.icon,
          onClick: (e?: React.MouseEvent) => {
            e?.stopPropagation()
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: 'MF_L2',
                eventId: button.testLocator,
                buttonLabel: button.text ?? 'Start SIP',
                fundIsin,
                fundName
              }
            })
            if (data?.buttonDisabledStates?.sip?.disabledReason === SubActionDisabledReason.REGULAR) {
              openRegularSheet(RegularPlanSheetVariant.INVEST)
              return
            }
            handlePaymentPageOpen(PaymentPageType.SIP)
          },
          isDisabled: data?.buttonDisabledStates?.sip?.isDisabled,
          disabledReason: data?.buttonDisabledStates?.sip?.disabledReason
        }
      }

      return {
        testLocator: button.testLocator,
        text: button.text,
        icon: button.icon,
        onClick: (e?: React.MouseEvent) => {
          e?.stopPropagation()
        },
        isDisabled: false
      }
    })
  }, [
    data?.buttonDisabledStates?.buy?.isDisabled,
    data?.buttonDisabledStates?.sip?.isDisabled,
    data?.buttonDisabledStates?.buy?.disabledReason,
    data?.buttonDisabledStates?.sip?.disabledReason,
    openRegularSheet,
    fundIsin,
    fundName
  ])

  const withdrawButtons = useMemo((): SubActionButton[] => {
    return staticWithdrawButtons.map((button: WithdrawButtonConfig): SubActionButton => {
      if (button.type === 'one-time') {
        return {
          testLocator: button.testLocator,
          text: button.text,
          icon: button.icon,
          onClick: (e?: React.MouseEvent) => {
            e?.stopPropagation()
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: 'MF_L2',
                eventId: button.testLocator,
                buttonLabel: button.text ?? 'One-time',
                fundIsin,
                fundName
              }
            })
            if (data?.buttonDisabledStates?.sell?.disabledReason === SubActionDisabledReason.REGULAR) {
              openRegularSheet(RegularPlanSheetVariant.WITHDRAW)
              return
            }
            handlePaymentPageOpen(PaymentPageType.WITHDRAW)
          },
          isDisabled: data?.buttonDisabledStates?.sell?.isDisabled || data?.sell?.folioDetails?.length === 0,
          disabledReason: data?.buttonDisabledStates?.sell?.disabledReason
        }
      } else if (button.type === 'start-swp') {
        return {
          testLocator: button.testLocator,
          text: button.text,
          icon: button.icon,
          onClick: (e?: React.MouseEvent) => {
            e?.stopPropagation()
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: 'MF_L2',
                eventId: button.testLocator,
                buttonLabel: button.text ?? 'Start SWP',
                fundIsin,
                fundName
              }
            })
            if (data?.buttonDisabledStates?.swp?.disabledReason === SubActionDisabledReason.REGULAR) {
              openRegularSheet(RegularPlanSheetVariant.WITHDRAW)
              return
            }
            handlePaymentPageOpen(PaymentPageType.SWP)
          },
          isDisabled: data?.buttonDisabledStates?.swp?.isDisabled,
          disabledReason: data?.buttonDisabledStates?.swp?.disabledReason
        }
      }

      return {
        testLocator: button.testLocator,
        text: button.text,
        icon: button.icon,
        onClick: (e?: React.MouseEvent) => {
          e?.stopPropagation()
        },
        isDisabled: false
      }
    })
  }, [
    data?.buttonDisabledStates?.sell?.isDisabled,
    data?.buttonDisabledStates?.swp?.isDisabled,
    data?.buttonDisabledStates?.sell?.disabledReason,
    data?.buttonDisabledStates?.swp?.disabledReason,
    data?.sell?.folioDetails?.length,
    openRegularSheet,
    fundIsin,
    fundName
  ])

  const moreOptionsButtons = useMemo((): SubActionButton[] => {
    return staticMoreOptionsButtons.map((button: MoreOptionsButtonConfig): SubActionButton => {
      if (button.type === 'transfer') {
        return {
          testLocator: button.testLocator,
          text: button.text,
          icon: button.icon,
          onClick: (e?: React.MouseEvent) => {
            e?.stopPropagation()
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: 'MF_L2',
                eventId: button.testLocator,
                buttonLabel: button.text ?? 'Transfer',
                fundIsin,
                fundName
              }
            })
            if (
              !data?.buttonDisabledStates?.transfer?.isDisabled &&
              data?.buttonDisabledStates?.transfer?.disabledReason
            ) {
              openTransferLearnMoreSheet()
              return
            }
            handlePaymentPageOpen(PaymentPageType.TRANSFER)
          },
          isDisabled: data?.buttonDisabledStates?.transfer?.isDisabled,
          // @ts-expect-error - TODO: fix this
          disabledReason: data?.buttonDisabledStates?.transfer?.disabledReason
        }
      }

      return {
        testLocator: button.testLocator,
        text: button.text,
        icon: button.icon,
        onClick: (e?: React.MouseEvent) => {
          e?.stopPropagation()
        },
        isDisabled: false
      }
    })
  }, [data?.buttonDisabledStates?.transfer?.isDisabled, fundIsin, fundName])

  return {
    investButtons,
    withdrawButtons,
    moreOptionsButtons
  }
}
